import { isApiError } from "@pokifolio/api-client";
import {
  type Assessment,
  AssessmentInputSchema,
  CHECK_ANSWERS,
  type CardType,
  type CheckAnswer,
  GRADING_COMPANIES,
  type GradingCompany,
  RAW_CONDITIONS,
  type RawCondition,
  type ResearchRecord,
} from "@pokifolio/contracts";
import { type SubmitEvent, useId, useState } from "react";

import { Alert, ErrorAlert } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Field } from "../../components/ui/field";
import { Input, Select, Textarea } from "../../components/ui/input";
import { serverFieldError } from "../../lib/errors";
import { type FieldErrors, validate } from "../../lib/forms";
import { useCurrentUser } from "../auth/session";
import { WEAR_LABELS, formatDateTime } from "../search/format";
import { suggestAssessment } from "./assessment-suggestion";
import { editRule } from "./edit-rule";
import { Fact } from "./fact";
import { CHECK_ANSWER_LABELS, GRADING_COMPANY_LABELS, RAW_CONDITION_LABELS } from "./format";
import { useSaveAssessment } from "./research-queries";

type Question = "scratches" | "whitening" | "bends" | "surface";

const QUESTIONS: readonly { key: Question; label: string; hint: string }[] = [
  { key: "scratches", label: "Scratches", hint: "On the front or the back." },
  { key: "whitening", label: "Whitening", hint: "White spots or chips on the edges and corners." },
  { key: "bends", label: "Bends or creases", hint: "Any bend, crease or dent in the card." },
  { key: "surface", label: "Surface damage", hint: "Stains, marks, scuffs or print lines." },
];

interface FormState {
  readonly cardType: CardType;
  readonly overall: RawCondition | "";
  readonly scratches: CheckAnswer | "";
  readonly whitening: CheckAnswer | "";
  readonly bends: CheckAnswer | "";
  readonly surface: CheckAnswer | "";
  readonly gradingCompany: GradingCompany | "";
  readonly grade: string;
  readonly certificateNumber: string;
  readonly notes: string;
}

const EMPTY_FORM: FormState = {
  cardType: "raw",
  overall: "",
  scratches: "",
  whitening: "",
  bends: "",
  surface: "",
  gradingCompany: "",
  grade: "",
  certificateNumber: "",
  notes: "",
};

/** The last check, or a starting point read from the listing's words. */
function startingForm(research: ResearchRecord): FormState {
  const a = research.assessment;
  if (a !== null) {
    return {
      cardType: a.cardType,
      overall: a.overall ?? "",
      scratches: a.scratches ?? "",
      whitening: a.whitening ?? "",
      bends: a.bends ?? "",
      surface: a.surface ?? "",
      gradingCompany: a.gradingCompany ?? "",
      grade: a.grade === null ? "" : String(a.grade),
      certificateNumber: a.certificateNumber ?? "",
      notes: a.notes ?? "",
    };
  }
  const suggestion = suggestAssessment(research.listing);
  if (suggestion === null) return EMPTY_FORM;
  return suggestion.cardType === "raw"
    ? { ...EMPTY_FORM, overall: suggestion.overall }
    : {
        ...EMPTY_FORM,
        cardType: "graded",
        gradingCompany: suggestion.gradingCompany,
        grade: String(suggestion.grade),
      };
}

/**
 * The answers for the chosen card type only; blanks are left out so the shared rules can say what is missing.
 * Checked with AssessmentInputSchema before anything is sent.
 */
function toInput(form: FormState): Record<string, unknown> {
  const pick = (value: string): string | undefined => (value === "" ? undefined : value);
  if (form.cardType === "raw") {
    return {
      cardType: "raw",
      overall: pick(form.overall),
      scratches: pick(form.scratches),
      whitening: pick(form.whitening),
      bends: pick(form.bends),
      surface: pick(form.surface),
      notes: form.notes,
    };
  }
  return {
    cardType: "graded",
    gradingCompany: pick(form.gradingCompany),
    grade: form.grade.trim() === "" ? undefined : Number(form.grade),
    certificateNumber: form.certificateNumber,
    notes: form.notes,
  };
}

/** The condition check on a research record: answers judged from the listing's photos, kept as versions. */
export function ConditionCheck({ research }: { research: ResearchRecord }) {
  const me = useCurrentUser();
  const rule = editRule(research, me, "the condition check");
  const [editing, setEditing] = useState(false);
  const save = useSaveAssessment(research.id);
  const headingId = useId();
  const { assessment } = research;

  const startEditing = () => {
    save.reset();
    setEditing(true);
  };

  return (
    <Card>
      <section aria-labelledby={headingId} className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id={headingId} className="text-base font-semibold text-foreground">
            Condition check
          </h2>
          {assessment !== null && rule.allowed && !editing && (
            <Button variant="secondary" size="sm" onClick={startEditing}>
              Change condition check
            </Button>
          )}
        </div>

        {!editing && save.isError && <ErrorAlert error={save.error} />}

        {editing ? (
          <AssessmentForm
            research={research}
            save={save}
            onDone={() => setEditing(false)}
            onCancel={() => {
              save.reset();
              setEditing(false);
            }}
          />
        ) : assessment === null ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">Not checked yet.</p>
            {rule.allowed && <Button onClick={startEditing}>Start condition check</Button>}
          </div>
        ) : (
          <AssessmentSummary assessment={assessment} research={research} currentUserId={me.user.id} />
        )}

        {!rule.allowed && rule.reason !== null && (
          <p className="text-xs text-muted-foreground">{rule.reason}</p>
        )}
      </section>
    </Card>
  );
}

interface AssessmentFormProps {
  readonly research: ResearchRecord;
  readonly save: ReturnType<typeof useSaveAssessment>;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}

function AssessmentForm({ research, save, onDone, onCancel }: AssessmentFormProps) {
  const [form, setForm] = useState<FormState>(() => startingForm(research));
  const [errors, setErrors] = useState<FieldErrors>({});
  const suggestion = research.assessment === null ? suggestAssessment(research.listing) : null;
  const ids = { overall: useId(), company: useId(), grade: useId(), certificate: useId(), notes: useId() };

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const errorFor = (key: string): string | undefined =>
    errors[key] ?? serverFieldError(save.error, `assessment.${key}`);
  const fieldErrorShown =
    Object.keys(errors).length > 0 ||
    (isApiError(save.error) && save.error.fieldErrors.some((e) => e.path.startsWith("assessment.")));

  function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    const result = validate(AssessmentInputSchema, toInput(form));
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    save.mutate(
      { assessment: result.data, version: research.version },
      {
        onSuccess: onDone,
        // Someone else changed the record meanwhile: show their version and the explanation.
        onError: (error) => {
          if (isApiError(error) && error.status === 409) onDone();
        },
      },
    );
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-5">
      <p className="text-sm text-muted-foreground">
        Look closely at the listing’s photos. Answer “Can't see” when the photos do not show it clearly.
      </p>
      {suggestion !== null && (
        <Alert tone="info">Filled in from {suggestion.because}. Check the photos before saving.</Alert>
      )}

      <Choices
        legend="Card type"
        options={[
          { value: "raw", label: "Raw (not graded)" },
          { value: "graded", label: "Graded (in a grading company’s case)" },
        ]}
        value={form.cardType}
        onChange={(value) => set("cardType", value)}
      />

      {form.cardType === "raw" ? (
        <>
          <Field id={ids.overall} label="Overall condition" error={errorFor("overall")}>
            <Select
              value={form.overall}
              onChange={(e) => set("overall", e.target.value as RawCondition | "")}
              className="max-w-xs"
            >
              <option value="">Choose…</option>
              {RAW_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {RAW_CONDITION_LABELS[c]}
                </option>
              ))}
            </Select>
          </Field>
          {QUESTIONS.map((q) => (
            <Choices
              key={q.key}
              legend={q.label}
              hint={q.hint}
              options={CHECK_ANSWERS.map((a) => ({ value: a, label: CHECK_ANSWER_LABELS[a] }))}
              value={form[q.key]}
              onChange={(value) => set(q.key, value)}
              error={errorFor(q.key)}
            />
          ))}
        </>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <Field id={ids.company} label="Grading company" error={errorFor("gradingCompany")}>
            <Select
              value={form.gradingCompany}
              onChange={(e) => set("gradingCompany", e.target.value as GradingCompany | "")}
            >
              <option value="">Choose…</option>
              {GRADING_COMPANIES.map((c) => (
                <option key={c} value={c}>
                  {GRADING_COMPANY_LABELS[c]}
                </option>
              ))}
            </Select>
          </Field>
          <Field id={ids.grade} label="Grade" hint="1 to 10, e.g. 9 or 9.5" error={errorFor("grade")}>
            <Input
              type="number"
              inputMode="decimal"
              min={1}
              max={10}
              step={0.5}
              value={form.grade}
              onChange={(e) => set("grade", e.target.value)}
            />
          </Field>
          <Field
            id={ids.certificate}
            label="Certificate number (optional)"
            error={errorFor("certificateNumber")}
          >
            <Input
              value={form.certificateNumber}
              onChange={(e) => set("certificateNumber", e.target.value)}
              autoComplete="off"
            />
          </Field>
        </div>
      )}

      <Field id={ids.notes} label="Notes (optional)" error={errorFor("notes")}>
        <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} />
      </Field>

      {save.isError && !fieldErrorShown && <ErrorAlert error={save.error} />}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save condition check"}
        </Button>
        <Button variant="ghost" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

interface ChoicesProps<T extends string> {
  readonly legend: string;
  readonly hint?: string;
  readonly options: readonly { value: T; label: string }[];
  readonly value: T | "";
  readonly onChange: (value: T) => void;
  readonly error?: string | undefined;
}

/** A question answered by picking one option (radio buttons), with its error under it. */
function Choices<T extends string>({ legend, hint, options, value, onChange, error }: ChoicesProps<T>) {
  const name = useId();
  const messageId = useId();
  return (
    <fieldset
      className="flex flex-col gap-1"
      {...(error === undefined ? {} : { "aria-describedby": messageId })}
    >
      <legend className="text-sm font-medium text-foreground">{legend}</legend>
      {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
      <div className="mt-1 flex flex-wrap gap-x-5 gap-y-2">
        {options.map((option) => (
          <label key={option.value} className="inline-flex items-center gap-1.5 text-sm text-foreground">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
              className="size-4 accent-primary"
            />
            {option.label}
          </label>
        ))}
      </div>
      {error !== undefined && (
        <p id={messageId} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </fieldset>
  );
}

interface AssessmentSummaryProps {
  readonly assessment: Assessment;
  readonly research: ResearchRecord;
  readonly currentUserId: string;
}

function AssessmentSummary({ assessment: a, research, currentUserId }: AssessmentSummaryProps) {
  const wear = research.listing.wear;
  const answer = (value: CheckAnswer | null) =>
    value === null ? "Not answered" : CHECK_ANSWER_LABELS[value];
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <Fact label="Card type">{a.cardType === "raw" ? "Raw (not graded)" : "Graded"}</Fact>
        {a.cardType === "raw" ? (
          <>
            <Fact label="Overall condition">
              {a.overall === null ? "Not answered" : RAW_CONDITION_LABELS[a.overall]}
            </Fact>
            {QUESTIONS.map((q) => (
              <Fact key={q.key} label={q.label}>
                {answer(a[q.key])}
              </Fact>
            ))}
          </>
        ) : (
          <>
            <Fact label="Grading company">
              {a.gradingCompany === null ? "Not given" : GRADING_COMPANY_LABELS[a.gradingCompany]}
            </Fact>
            <Fact label="Grade">{a.grade === null ? "Not given" : String(a.grade)}</Fact>
            <Fact label="Certificate number">{a.certificateNumber ?? "Not given"}</Fact>
          </>
        )}
        {a.notes !== null && <Fact label="Notes">{a.notes}</Fact>}
      </dl>
      {a.cardType === "raw" && wear !== null && wear !== a.overall && (
        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
          ⚠ The listing itself says “{WEAR_LABELS[wear]}”.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        Version {a.version} · checked by {a.checkedBy.id === currentUserId ? "you" : a.checkedBy.name} ·{" "}
        {formatDateTime(a.checkedAt)}
      </p>
    </div>
  );
}
